import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useSettings } from '../modules/Settings/SettingsProvider';

export enum ViewMode {
  Normal = 'normal',
  Replay = 'replay',
  Detail = 'detail',
}

// This enum is specific to the detail view
export enum DetailView {
  Vessel = 'vessel',
  Aton = 'aton',
}

interface ViewModeContextProps {
  viewMode: ViewMode;
  setViewMode: (newViewMode: ViewMode, subCategory?: DetailView | null) => void;
  detailView: DetailView | null;
}

const ViewModeContext = createContext<ViewModeContextProps | undefined>(
  undefined
);

interface ViewModeProviderProps {
  children: ReactNode;
}

export const ViewModeProvider = ({ children }: ViewModeProviderProps) => {
  const [viewMode, _setViewMode] = useState<ViewMode>(ViewMode.Normal);
  const [detailView, setDetailView] = useState<DetailView | null>(null);
  const { saveMapState } = useSettings();

  const setViewMode = useCallback(
    (newViewMode: ViewMode, subCategory: DetailView | null = null) => {
      _setViewMode(newViewMode);
      if (newViewMode === ViewMode.Detail) {
        // Save the current map state before switching to detail view, so that when the user returns to the map, it can restore the state
        saveMapState();
        setDetailView(subCategory);
      } else {
        setDetailView(null);
      }
    },
    [saveMapState]
  );

  const value = {
    viewMode,
    setViewMode,
    detailView,
  };

  return (
    <ViewModeContext.Provider value={value}>
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewMode = () => {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
};
